import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Form } from "react-final-form";
import { IDocument } from "../../models/entities/document.model";
import UiButton from "../button/Button";
import UiInputText from "../input-text/InputText.component";
import UiInputTextarea from "../input-textarea/InputTextarea.component";
import { Validations } from "../../services/form/validations.service";
import FormSection from "../form-section/FormSection.component";
import { useUpdateDocument } from "../../hooks/useUpdateDocument";

interface ModalDocumentProps {
	isVisible: boolean;
	document: IDocument | null;
	onClose: () => void;
	onComplete: () => void;
}

const ModalDocument = ({ isVisible, document, onClose, onComplete }: ModalDocumentProps) => {
	const { t } = useTranslation("common");
	const updateDocumentMutation = useUpdateDocument();

	const [error, setError] = useState(false);

	// Sync form data when document changes
	useEffect(() => {
		if (document) {
			setError(false);
		}
	}, [document]);

	const handleSubmit = async (data: { name: string; notes: string | null }) => {
		if (!document) return;

		setError(false);

		try {
			await updateDocumentMutation.mutateAsync({
				documentId: document.id!,
				displayName: data.name,
				notes: data.notes || null,
			});
			onComplete();
		} catch (err) {
			setError(true);
		}
	};

	if (!document) return null;

	return (
		<Dialog
			header={t("PARTICIPANT.DOCUMENTS.MODAL_EDIT.TITLE")}
			visible={isVisible}
			breakpoints={{ "960px": "75vw", "640px": "100vw" }}
			style={{ width: "50vw" }}
			modal={true}
			focusOnShow={false}
			draggable={false}
			closable={true}
			onHide={onClose}
		>
			<Form
				onSubmit={handleSubmit}
				initialValues={{
					name: document.name || "",
					notes: document.notes || null,
				}}
				render={({ handleSubmit, submitting }) => (
					<form onSubmit={handleSubmit}>
						<FormSection>
							<div className="row">
								{/* Document Name */}
								<div className="col-12">
									<UiInputText
										id="name"
										placeholder="PARTICIPANT.DOCUMENTS.MODAL_EDIT.FORM_FIELDS.name"
										label="PARTICIPANT.DOCUMENTS.MODAL_EDIT.FORM_FIELDS.name"
										name="name"
										validations={[Validations.required]}
										disabled={submitting}
									/>
								</div>

								{/* Notes */}
								<div className="col-12">
									<UiInputTextarea
										id="notes"
										placeholder="PARTICIPANT.DOCUMENTS.MODAL_EDIT.FORM_FIELDS.notes"
										label="PARTICIPANT.DOCUMENTS.MODAL_EDIT.FORM_FIELDS.notes"
										name="notes"
										disabled={submitting}
									/>
								</div>
							</div>
						</FormSection>

						{/* Error message */}
						{error && (
							<div className="field-message field-message--danger">
								{t("PARTICIPANT.DOCUMENTS.MODAL_EDIT.ERROR")}
							</div>
						)}

						{/* Actions */}
						<div className="action-buttons">
							<UiButton
								type="button"
								label="UI_COMPONENTS.BUTTONS.CANCEL"
								className="p-button-outlined p-button-rounded"
								onClick={onClose}
								disabled={submitting}
							/>
							<UiButton
								type="submit"
								label="UI_COMPONENTS.BUTTONS.SAVE"
								className="p-button-rounded"
								disabled={submitting}
								loading={submitting}
							/>
						</div>
					</form>
				)}
			/>
		</Dialog>
	);
};

export default ModalDocument;
